import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { applyActionCode } from "firebase/auth";
import { CheckCircle, TriangleAlert, XCircle } from "lucide-react";
import ResetPassword from "../ResetPassword";
import { getAuthInstance } from "../firebase";
import Button from "../components/Button/Button";
import { AppLoader } from "../components/AppLoader";

const AuthAction = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const auth = getAuthInstance();

  const [status, setStatus] = useState("verifyButton");
  const [screen, setScreen] = useState("verifyEmail");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const redirectTimeoutRef = useRef(null);

  const scheduleRedirect = useCallback(() => {
    if (redirectTimeoutRef.current) clearTimeout(redirectTimeoutRef.current);
    redirectTimeoutRef.current = setTimeout(() => navigate("/"), 5000);
  }, [navigate]);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) clearTimeout(redirectTimeoutRef.current);
    };
  }, []);

  const handleVerify = async (oobCode) => {
    setLoading(true);
    applyActionCode(auth, oobCode)
      .then(() => {
        setStatus("success");
        scheduleRedirect();
      })
      .catch((error) => {
        setStatus("error");
        scheduleRedirect();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const oobCode = params.get("oobCode");

    const mode = params.get("mode");

    if (!oobCode || !mode) {
      setStatus("invalid");
      scheduleRedirect();
      return;
    }
    setCode(oobCode);

    if (mode === "verifyEmail") {
      setScreen("verifyEmail");
      setStatus("verifyButton");
    } else {
      setScreen("resetPassword");
    }
  }, [params, navigate, scheduleRedirect]);

  return screen === "verifyEmail" ? (
    <div className="mt-40 flex flex-col items-center justify-center h-full">
      {status === "verifying" && (
        <div className="flex justify-center items-cente gap-2">
          <AppLoader color="black" />
          <span>Verifying your email...</span>
        </div>
      )}
      {status === "verifyButton" && (
        <div className="flex justify-center items-center gap-2">
          <Button onClick={() => handleVerify(code)}>
            {loading ? <AppLoader color="white" /> : "Click here"}
          </Button>{" "}
          to verify your email.
        </div>
      )}
      {status === "success" && (
        <div className="flex justify-center items-cente gap-2 bg-[#E0F0E4] p-4 text-sm rounded-xl mb-6 text-center text-[#377E36]">
          <CheckCircle className="w-6 h-6" />
          <span>Email verified successfully! Redirecting to login...</span>
        </div>
      )}
      {status === "error" && (
        <div className="flex justify-center items-cente gap-2 bg-[#f0e0e0] p-4 text-sm rounded-xl mb-6 text-center text-[#7e3636]">
          <XCircle className="w-6 h-6" />
          <span>Verification failed or expired. Redirecting to login...</span>
        </div>
      )}
      {status === "invalid" && (
        <div className="flex justify-center items-cente gap-2 bg-[#f0ece0] p-4 text-sm rounded-xl mb-6 text-center text-[#7e6b36]">
          <TriangleAlert className="w-6 h-6" />
          <span>Invalid request. Redirecting to login...</span>
        </div>
      )}
    </div>
  ) : (
    <ResetPassword oobCode={code} />
  );
};

export default AuthAction;
